/**
 * M16 - Add/Edit Vendor field rules, mirrored from
 * server/src/modules/vendor/vendor.validation.ts on the server; keep the two in step.
 *
 * Each validator returns null when the value is acceptable (blank counts as acceptable for the
 * optional fields) or a message written to be shown next to the field.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function validateVendorEmail(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  return EMAIL_RE.test(v) ? null : 'Enter a valid email address, e.g. name@company.ca.';
}

/**
 * North American numbers: 10 digits, or 11 with a leading 1. Spaces, dashes, dots, brackets
 * and a leading + are allowed, as is an extension ("x123", "ext. 123").
 */
export function validateVendorPhone(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  if (!v) return null;
  const withoutExt = v.replace(/\s*(?:x|ext\.?|extension)\s*\d{1,6}$/i, '');
  if (/[^\d\s().+-]/.test(withoutExt)) {
    return 'Enter a 10-digit phone number, e.g. 250-555-0123.';
  }
  const digits = withoutExt.replace(/\D/g, '');
  const ok = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  return ok ? null : 'Enter a 10-digit phone number, e.g. 250-555-0123.';
}

export function validateVendorLegalName(value: string | null | undefined): string | null {
  return (value ?? '').trim() ? null : 'Legal name is required.';
}

export interface VendorFieldErrors {
  [field: string]: string;
}

/**
 * `requireLegalName` is true on create, and on edit whenever the payload carries legalName -
 * an edit that leaves it out is a partial update and must not be refused for a legacy vendor
 * that never had one.
 */
export function validateVendorFields(
  data: {
    legalName?: string | null;
    contactEmail?: string | null;
    phoneNumber?: string | null;
  },
  options: { requireLegalName: boolean }
): VendorFieldErrors {
  const errors: VendorFieldErrors = {};
  if (options.requireLegalName) {
    const e = validateVendorLegalName(data.legalName);
    if (e) errors.legalName = e;
  }
  const email = validateVendorEmail(data.contactEmail);
  if (email) errors.contactEmail = email;
  const phone = validateVendorPhone(data.phoneNumber);
  if (phone) errors.phoneNumber = phone;
  return errors;
}
